import { useEffect, useState } from 'react';
import { teachingLanguages, type LanguageCode } from '../i18n/languages';
import { getAuth, updateProfile } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { notifyIdentityChanged } from '../auth/identity';
import { app, db } from './config';

/**
 * C1 — writes the name to BOTH places a non-empty name is required to read
 * from: the Auth profile (identity.ts's primary source) and
 * users/{uid}.displayName (identity.ts's fallback, and what the rules'
 * hasOnly(['displayName']) update on users/{uid} exists for — firestore.rules
 * :176-178 is the only field a client may ever write there).
 *
 * The Firestore write is what actually unblocks the app: identity.ts's
 * fallback listener is already subscribed to this exact document whenever
 * the Auth profile's name is empty, so this landing is what flips
 * `displayName` from '' to something real — no separate "refresh identity"
 * step, and no dependency on updateProfile succeeding for that to happen.
 * Both are attempted regardless, so a transient failure on one side does not
 * silently skip the other.
 */
export type Gender = 'male' | 'female';

export type ProfileDraft = {
  displayName: string;
  age: number;
  /** Present only once said. Optional since App Review 5.1.1(v). */
  gender?: Gender;
  /** Present only once the reader has said where they are at school. */
  country?: string;
  grade?: number;
};

export async function submitDisplayName(displayName: string): Promise<void> {
  return submitProfile({ displayName, age: 0, gender: 'male' }, { nameOnly: true });
}

/**
 * Writes the profile a person may edit about themselves.
 *
 * Age and gender go only to Firestore — the Auth profile has no field for
 * either, and duplicating them would create a second copy to keep in sync.
 * The rules accept exactly these three keys (see validProfileEdit); anything
 * else on users/{uid}, roles above all, stays server-only.
 */
export async function submitProfile(
  draft: ProfileDraft,
  opts: { nameOnly?: boolean } = {}
): Promise<void> {
  const user = getAuth(app).currentUser;
  if (!user) throw new Error('unauthenticated');
  const { displayName, age, gender, country, grade } = draft;
  const fields = opts.nameOnly
    ? { displayName }
    : {
      displayName,
      age,
      // Unsaid is left alone, never written as null: the rules take only
      // 'male' or 'female', and a gender set on another device must survive.
      ...(gender ? { gender } : {}),
      // Only written when said. The rules accept these five keys and no
      // others; an undefined would be sent as a delete, wiping a grade the
      // reader set on another device.
      ...(country && grade ? { country, grade } : {}),
    };
  await Promise.all([
    updateProfile(user, { displayName }),
    updateDoc(doc(db, 'users', user.uid), fields),
  ]);
  notifyIdentityChanged();
}


/**
 * Records a person's gender, on its own, at the moment it is needed.
 *
 * Signup no longer asks (App Review 5.1.1(v), 2026-09-19). What does need it
 * is a live session: they are same-gender, admitStudent refuses a pair whose
 * genders are not both known, and a pupil with none is shown no teachers.
 * So the question is put right there — GenderPrompt — and answered with this.
 *
 * Checked here as well as by the rules so a bad value is named, not reported
 * as a bare permission-denied.
 */
export async function setMyGender(gender: Gender): Promise<void> {
  if (gender !== 'male' && gender !== 'female') throw new Error('invalid-gender');
  const user = getAuth(app).currentUser;
  if (!user) throw new Error('unauthenticated');
  await updateDoc(doc(db, 'users', user.uid), { gender });
}

/** What users/{uid} holds about a person, as an edit form needs to read it. */
export type StoredProfile = {
  displayName: string;
  /** Null for an account that predates the field, NOT a guessed default. */
  age: number | null;
  gender: Gender | null;
  /*
   * Where school put this person. Server-written (enrolClass, createSchool),
   * never editable here — but read constantly: the class board asks which
   * class to draw, and a manager's screens ask which school they run.
   *
   * These were missing while every caller read them anyway. The screens
   * type-cast the hook's result and got undefined, so a pupil in a class was
   * told they were in none and a manager was shown an empty school. Nothing
   * caught it because every test mocks this hook — it took running the app
   * against real data to see.
   */
  classId: string | null;
  schoolId: string | null;
  /**
   * The languages this person teaches in — م-7-02. Theirs to say, and said
   * about themselves; it decides which pupils find them, not what they may do.
   */
  teachingLanguages: LanguageCode[];
  /**
   * True while this account has no phone on it — see functions/guests.js.
   * Read so a screen can offer to keep the account, never to withhold
   * anything from a guest.
   */
  isGuest: boolean;
  loading: boolean;
};

const EMPTY: Omit<StoredProfile, 'loading'> = {
  displayName: '', age: null, gender: null, classId: null, schoolId: null,
  teachingLanguages: [], isGuest: false,
};

/**
 * The profile a person may edit about themselves.
 *
 * Read live rather than passed in: identity.ts carries the name and nothing
 * else, and age and gender live only in Firestore. `loading` matters — a form
 * that renders a blank age while the real one is still in flight invites
 * someone to save over their own data with nothing.
 *
 * An unrecognised gender and a missing age come back null rather than
 * defaulted. Guessing 'male' would write a fact nobody stated the moment the
 * form was saved.
 */
export function useMyProfile(uid: string | null): StoredProfile {
  const [state, setState] = useState<StoredProfile>({ ...EMPTY, loading: true });
  useEffect(() => {
    if (!uid) { setState({ ...EMPTY, loading: false }); return undefined; }
    setState({ ...EMPTY, loading: true });
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const data = snap.data() ?? {};
        const age = typeof data.age === 'number' ? data.age : null;
        const gender = data.gender === 'male' || data.gender === 'female' ? data.gender : null;
        setState({
          displayName: typeof data.displayName === 'string' ? data.displayName : '',
          age,
          gender,
          classId: typeof data.classId === 'string' ? data.classId : null,
          teachingLanguages: teachingLanguages(data.teachingLanguages),
          isGuest: data.isGuest === true,
          schoolId: typeof data.schoolId === 'string' ? data.schoolId : null,
          loading: false,
        });
      },
      // A read that fails leaves the form empty and NOT loading, so it says
      // so rather than spinning forever.
      () => setState({ ...EMPTY, loading: false })
    );
  }, [uid]);
  return state;
}

/**
 * A معلم saying which languages they teach in — م-7-02.
 *
 * Written by the teacher about themselves, like their own name: the rules
 * accept it in that shape and refuse anything outside the app's four, so a
 * teacher cannot list themselves under a language the app cannot display.
 *
 * It reaches pupils only when a session opens, which copies it onto the
 * session — a pupil may not read another person's document at all.
 */
export async function saveTeachingLanguages(codes: unknown): Promise<void> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('unauthenticated');
  await updateDoc(doc(db, 'users', user.uid), {
    teachingLanguages: teachingLanguages(codes),
  });
}

/**
 * Where the reader sits in school, said on its own — the grade picker
 * (redesign e4). «حدّد صفّك» used to do nothing, so a guest or a self
 * sign-up never had a grade and the syllabus, map and practice stayed empty.
 *
 * Only these two keys: submitProfile also writes the name and the age, and a
 * guest has no age the rules would accept. Checked here as well as by the
 * rules (validProfileEdit) so a bad value is named rather than reported as a
 * bare permission-denied. A managed child's own write is refused by the
 * rules — their guardian sets it.
 */
export async function saveMySchooling(country: string, grade: number): Promise<void> {
  const code = String(country ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) throw new Error('invalid-country');
  if (!Number.isInteger(grade) || grade < 1 || grade > 12) throw new Error('invalid-grade');
  const user = getAuth(app).currentUser;
  if (!user) throw new Error('unauthenticated');
  await updateDoc(doc(db, 'users', user.uid), { country: code, grade });
}
